import {
  AlertBannerCodeMap,
  ALERT_BANNER_CODE,
} from "@kivunova/kivufrontendcommon";

/**
 * Map backend error codes (or thrown error messages) to user-friendly messages.
 * Use wherever we display error.message to the user (e.g. login form, inline alerts).
 */

const FALLBACK_MESSAGE = "Something went wrong. Please try again.";

/**
 * Returns a friendly message for a given backend error code or thrown message.
 * Uses AlertBannerCodeMap when the code matches; otherwise a local map or the message as-is if it looks like prose.
 */
export function getFriendlyErrorMessage(
  errorCodeOrMessage: string | undefined,
): string {
  if (!errorCodeOrMessage || typeof errorCodeOrMessage !== "string") {
    return FALLBACK_MESSAGE;
  }
  const raw = errorCodeOrMessage.trim();
  const code = raw.toUpperCase();

  // Use shared map if the code exists (e.g. from GraphQL extensions)
  const fromCommon =
    AlertBannerCodeMap[code as keyof typeof AlertBannerCodeMap];
  if (fromCommon?.value) {
    return fromCommon.value;
  }

  // Local mapping for codes we throw or receive (e.g. login flow)
  const localMap: Record<string, string> = {
    [ALERT_BANNER_CODE.ACCESS_DENIED]:
      "This account does not have admin access. Use the main app for company and store operations.",
    [ALERT_BANNER_CODE.UNAUTHORIZED]:
      "You are not authorized. Please check your email and password and try again.",
    ACCESS_DENIED:
      "This account does not have admin access. Use the main app for company and store operations.",
    UNAUTHORIZED:
      "You are not authorized. Please check your email and password and try again.",
    [ALERT_BANNER_CODE.INVALID_CREDENTIALS]:
      "Invalid email or password. Please try again.",
    INVALID_CREDENTIALS: "Invalid email or password. Please try again.",
    [ALERT_BANNER_CODE.ACCOUNT_NOT_ACTIVE]:
      "This account is not active. Please contact support.",
    ACCOUNT_NOT_ACTIVE: "This account is not active. Please contact support.",
    [ALERT_BANNER_CODE.FORBIDDEN]:
      "You do not have permission to perform this action.",
    FORBIDDEN: "You do not have permission to perform this action.",
    [ALERT_BANNER_CODE.SERVICE_UNAVAILABLE]:
      "The service is temporarily unavailable. Please try again later.",
    SERVICE_UNAVAILABLE:
      "The service is temporarily unavailable. Please try again later.",
    [ALERT_BANNER_CODE.VALIDATION_ERROR]:
      "Please check your input and try again.",
    VALIDATION_ERROR: "Please check your input and try again.",
  };

  const mapped = localMap[code] ?? localMap[code.replace(/-/g, "_")];
  if (mapped) return mapped;

  // Already a friendly message (e.g. thrown by fetchGraphQL with errorMessage.value)
  if (raw.length > 40 || raw.includes(" ")) {
    return raw;
  }
  return FALLBACK_MESSAGE;
}
